import type { RefObject } from 'react';
import { createContext, useContext } from 'react';

import type { Color } from '../../../../types/colors';
import type { Purpose, Size } from '../../../../types/layout';
import type { useFocusManagement } from '../Input/useFocusManagement';
import type { DisablePresets } from '../types';

/**
 * Configuration context for SgDatePicker
 * Contains static configuration that rarely changes
 */
export interface DatePickerConfigContextValue {
  inputRef?: RefObject<HTMLInputElement | null>;
  locale: string;
  dateInputCultureInfo: string;
  dateFormat?: string;
  isControlled?: boolean;
  purpose: Purpose;
  size: Size;
  color?: Color;
  placeholder: string;
  disabled?: boolean;
  readonly?: boolean;
  yearControls?: boolean;
  inputId?: string;
  disablePresets?: DisablePresets;
  shouldAutoFocusCalendar?: boolean;
  customPresets?: string[] | Record<string, string | undefined>;
  presetsDisplayGrid: number;
  showClearButton?: boolean;
  focusManagement?: ReturnType<typeof useFocusManagement>;
}

const DatePickerConfigContext = createContext<DatePickerConfigContextValue | undefined>(undefined);

export const DatePickerConfigProvider = DatePickerConfigContext.Provider;

export function useDatePickerConfig(): DatePickerConfigContextValue {
  const context = useContext(DatePickerConfigContext);

  if (!context) {
    throw new Error('useDatePickerConfig must be used within DatePickerConfigProvider');
  }

  return context;
}
